export type QueueStage =
  | 'New'
  | 'Contacting'
  | 'Engaged'
  | 'App Started'
  | 'App Intake · App Received'
  | 'App Intake · Credit & Pricing'
  | 'Clear to Close'

export type LeadTask = {
  id: string
  label: string
  due: string
  done: boolean
}

export type Lead = {
  id: string
  name: string
  state: string
  amount: number
  detail: string
  stage: QueueStage
  accent: 'blue' | 'teal' | 'purple' | 'green'
  source?: string
  phone?: string
  email?: string
  loanPurpose?: string
  lastTouch?: string
  attempts?: string
  tasks?: LeadTask[]
}

export type ActivityItem = {
  id: string
  icon: 'home' | 'user' | 'message' | 'mail' | 'check'
  text: string
  emphasis: string
  time: string
}

export type FunnelStep = {
  label: string
  value: number
  max: number
  color: string
}

export const currentUser = {
  name: 'Milan R.',
  firstName: 'Milan',
  initials: 'MR',
  role: 'Loan Officer · DTC',
  org: 'United Mortgage Lending',
}

export const summaryStats = [
  {
    id: 'new-today',
    label: 'New today',
    value: '5',
    caption: '3 untouched',
    tone: 'default' as const,
  },
  {
    id: 'follow-ups',
    label: 'Follow-ups due',
    value: '7',
    caption: '1 overdue',
    tone: 'danger' as const,
  },
  {
    id: 'app-intake',
    label: 'App intake',
    value: '3',
    caption: 'submitted apps in intake',
    tone: 'default' as const,
  },
  {
    id: 'aging',
    label: 'Aging > 7 days',
    value: '4',
    caption: 'no touch in a week — save or release',
    tone: 'default' as const,
  },
]

export const leadStages = ['New', 'Contacting', 'Engaged', 'App Started'] as const
export type LeadStage = (typeof leadStages)[number]

export const queue: Lead[] = [
  {
    id: 'l1',
    name: 'Danielle Ferro',
    state: 'FL',
    amount: 385000,
    detail: 'New Bankrate lead · assigned 3m ago',
    stage: 'New',
    accent: 'blue',
    source: 'Bankrate',
    phone: '(305) 555-0142',
    email: 'danielle.ferro@email.com',
    loanPurpose: 'Purchase · Primary',
    lastTouch: 'Never contacted',
    attempts: '0 of 6 attempts',
    tasks: [
      { id: 'lt1', label: 'First call — speed to lead', due: 'Now', done: false },
      { id: 'lt2', label: 'Send intro text', due: 'Now', done: false },
    ],
  },
  {
    id: 'l2',
    name: 'Marcus Webb',
    state: 'NY',
    amount: 610000,
    detail: 'New Fincast lead · assigned 41m ago',
    stage: 'New',
    accent: 'blue',
    source: 'Fincast',
    phone: '(917) 555-0113',
    email: 'm.webb@email.com',
    loanPurpose: 'Refinance · Rate & term',
    lastTouch: 'Never contacted',
    attempts: '0 of 6 attempts',
    tasks: [{ id: 'lt3', label: 'First call — speed to lead', due: 'Now', done: false }],
  },
  {
    id: 'l3',
    name: 'Sandra Kim',
    state: 'NJ',
    amount: 505000,
    detail: 'New Bankrate lead · assigned 1m ago',
    stage: 'New',
    accent: 'blue',
    source: 'Bankrate',
    phone: '(201) 555-0187',
    email: 'sandra.kim@email.com',
    loanPurpose: 'Purchase · Primary',
    lastTouch: 'Never contacted',
    attempts: '0 of 6 attempts',
    tasks: [{ id: 'lt4', label: 'First call — speed to lead', due: 'Now', done: false }],
  },
  {
    id: 'l4',
    name: 'Aaron Whitfield',
    state: 'FL',
    amount: 512000,
    detail: 'New app submitted 22m ago — review the file',
    stage: 'App Started',
    accent: 'green',
    source: 'Bankrate',
    phone: '(407) 555-0166',
    email: 'awhitfield@email.com',
    loanPurpose: 'Purchase · Primary',
    lastTouch: 'App submitted 22m ago',
    attempts: '3 of 6 attempts',
    tasks: [{ id: 'lt5', label: 'Review the file — LN-1191', due: 'Today', done: false }],
  },
  {
    id: 'l5',
    name: 'Priya Natarajan',
    state: 'NJ',
    amount: 450000,
    detail: 'Follow-up today 2:00 PM · Call attempt 4 (evening)',
    stage: 'Contacting',
    accent: 'purple',
    source: 'Fincast',
    phone: '(908) 555-0129',
    email: 'priya.n@email.com',
    loanPurpose: 'Purchase · Primary',
    lastTouch: 'Voicemail · yesterday 6:10 PM',
    attempts: '3 of 6 attempts',
    tasks: [{ id: 'lt6', label: 'Call attempt 4 (evening)', due: 'Today 2:00 PM', done: false }],
  },
  {
    id: 'l6',
    name: 'Robert & Ana Silva',
    state: 'FL',
    amount: 329500,
    detail: 'Follow-up today 11:30 AM · Callback — close on app',
    stage: 'Engaged',
    accent: 'teal',
    source: 'Referral',
    phone: '(786) 555-0104',
    email: 'silva.family@email.com',
    loanPurpose: 'Refinance · Cash out',
    lastTouch: 'Text reply · 54m ago',
    attempts: '5 of 6 attempts',
    tasks: [{ id: 'lt7', label: 'Callback — close on app', due: 'Today 11:30 AM', done: false }],
  },
  {
    id: 'l7',
    name: 'Jasmine Cole',
    state: 'GA',
    amount: 275000,
    detail: 'Follow-up today 4:00 PM · Send pricing options',
    stage: 'App Started',
    accent: 'green',
    source: 'Bankrate',
    phone: '(678) 555-0198',
    email: 'jcole@email.com',
    loanPurpose: 'Purchase · Primary',
    lastTouch: 'Call · this morning',
    attempts: '4 of 6 attempts',
    tasks: [{ id: 'lt8', label: 'Send pricing options', due: 'Today 4:00 PM', done: false }],
  },
]

export type JumpPoint = {
  id: string
  label: string
  count: number
  caption: string
  to: string
  tone: 'default' | 'danger' | 'warn'
}

export const jumpPoints: JumpPoint[] = [
  {
    id: 'follow-ups',
    label: 'Follow-ups due',
    count: 7,
    caption: '1 overdue',
    to: '/follow-ups',
    tone: 'danger',
  },
  {
    id: 'new-leads',
    label: 'New leads',
    count: 5,
    caption: '3 untouched',
    to: '/leads',
    tone: 'default',
  },
  {
    id: 'stalled',
    label: 'Stalled apps',
    count: 2,
    caption: 'no movement in 7+ days',
    to: '/loans',
    tone: 'warn',
  },
  {
    id: 'in-process',
    label: 'In-process loans',
    count: 5,
    caption: '1 clear to close',
    to: '/loans',
    tone: 'default',
  },
]

export const activity: ActivityItem[] = [
  {
    id: 'a1',
    icon: 'home',
    emphasis: 'Aaron Whitfield',
    text: 'submitted his application — LN-1191 created',
    time: '22m ago',
  },
  {
    id: 'a2',
    icon: 'user',
    emphasis: 'Sandra Kim',
    text: 'assigned to you · Bankrate · NJ',
    time: '1m ago',
  },
  {
    id: 'a3',
    icon: 'message',
    emphasis: 'Robert Silva',
    text: 'replied: “Talked it over — call me at 11:30”',
    time: '54m ago',
  },
  {
    id: 'a4',
    icon: 'user',
    emphasis: 'Danielle Ferro',
    text: 'assigned to you · Bankrate · FL',
    time: '3m ago',
  },
  {
    id: 'a5',
    icon: 'mail',
    emphasis: 'Grace Lindqvist',
    text: 'opened your proposal (3rd time)',
    time: '1h ago',
  },
  {
    id: 'a6',
    icon: 'check',
    emphasis: 'Devon Price',
    text: 'is Clear to Close — closing 8/04',
    time: '2h ago',
  },
]

export const funnel: FunnelStep[] = [
  { label: 'Assigned', value: 128, max: 128, color: '#141b34' },
  { label: 'Contacted', value: 100, max: 128, color: '#3b4468' },
  { label: 'Engaged', value: 52, max: 128, color: '#ed6a0c' },
  { label: 'Converted', value: 22, max: 128, color: '#15803d' },
]

export const formatCurrency = (value: number): string =>
  `${value.toLocaleString('en-US')}`

/** Loan amount isn't captured for manually added leads until the first call. */
export const formatAmount = (value: number): string =>
  value > 0 ? formatCurrency(value) : 'Amount TBD'
